export { FeatureLayerUtils }

/**
 * Static class with helper methods for feature based layers
 * 
 * @memberof vef.map.layer.utils
 */
class FeatureLayerUtils {

    static canvasRenderer_ = null;

    static availableCrs = ["EPSG:3857", "EPSG:4326", "EPSG:3995", "EPSG:3031"];

    /**
     * Helper method to set the transparency between 0 and 1 of a feature based layer
     * 
     * @param {Layer} layer
     * @param {number} opacity
     */
    static setOpacity(layer, opacity) {
        layer.opacity = opacity;

        if (!(layer.activeMap_ in layer.mapLayers_)) return;

        switch (layer.activeMap_) {
            case "leaflet":
                const leafletLayer = layer.mapLayers_.leaflet;
                if (leafletLayer instanceof L.Path) {
                    leafletLayer.setStyle({ opacity: opacity, fillOpacity: opacity * layer.opacityRatio });
                } else if (leafletLayer instanceof L.FeatureGroup) {
                    leafletLayer.eachLayer(l => {
                        if (l instanceof L.Path) l.setStyle({ opacity: opacity, fillOpacity: opacity * layer.opacityRatio });
                    });
                }
                break;
        }
    }

    /**
     * Helper method to compute the bounds of feature layers
     * 
     * @param {Layer} layer 
     * @returns {object} bounds
     */
    static computeBounds(layer) {
        if (!(layer.activeMap_ in layer.mapLayers_)) return;

        try {
            switch (layer.activeMap_) {
                case "leaflet":
                    const leafletLayer = layer.mapLayers_.leaflet;
                    if ("getBounds" in leafletLayer) {
                        const bounds = leafletLayer.getBounds();
                        return {
                            min: {
                                x: bounds.getWest(),
                                y: bounds.getSouth()
                            },
                            max: {
                                x: bounds.getEast(),
                                y: bounds.getNorth()
                            },
                            crs: "CRS:84"
                        }
                    } else if ("getLatLng" in leafletLayer) {
                        const latLng = leafletLayer.getLatLng();
                        return {
                            min: {
                                x: latLng.lng - 2,
                                y: latLng.lat - 2,
                            },
                            max: {
                                x: latLng.lng + 2,
                                y: latLng.lat + 2,
                            },
                            crs: "CRS:84"
                        }
                    }
                    break;
            }
        } catch (e) {
            console.warn(e);
        }
        return null;
    }

    /**
     * Helper method to be called by feature based layers
     * to ceate the feature info object
     * 
     * @returns {Promise} feature info
     */
    static getFeatureInfo(layer) {
        return new Promise(resolve => {
            const response = {
                layer: layer,
                data: {
                    type: "FeatureCollection",
                    features: layer.clickedFeatures_
                }
            };

            // add crs if available, so the popup can be rendered with proper coordinates
            if (("geoJSON" in layer) && ("crs" in layer.geoJSON)) {
                response.data.crs = layer.geoJSON.crs;
            }

            // reset clicked features
            layer.clickedFeatures_ = [];

            resolve(response);
        });
    }

    /**
     * Get the static canvas renderer that is used by all feature layers. Depends on Leaflet Version 1.7.1
     * The default leaflet canvas is extended to allow clicking multiple features
     */
    static getCanvasRenderer() {
        if (FeatureLayerUtils.canvasRenderer_) return FeatureLayerUtils.canvasRenderer_;
        FeatureLayerUtils.canvasRenderer_ = new L.CanvasMod();
        return FeatureLayerUtils.canvasRenderer_;
    }

}